import React, { useEffect, useCallback } from 'react';

export interface TermsOfServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TermsOfServiceModal: React.FC<TermsOfServiceModalProps> = ({ isOpen, onClose }) => {
  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-modal-title"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-4xl max-h-[90vh] bg-slate-900 border border-slate-700 rounded-xl shadow-2xl overflow-hidden animate-fade-in">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-30 p-2 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
          aria-label="Close terms of service"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="overflow-y-auto max-h-[90vh] p-8">
          <h1 id="terms-modal-title" className="text-3xl font-bold text-white mb-6">
            Terms of Service
          </h1>

          <div className="prose prose-invert prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
              <p className="text-slate-300 mb-4">
                By accessing and using TradeRewind's services, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">2. Description of Service</h2>
              <p className="text-slate-300 mb-4">
                TradeRewind provides users with access to advanced trading analytics, market data visualization, AI-powered insights, and related services (the "Service"). The Service includes various features such as real-time market analysis, technical indicators, charting tools, and automated trading signals.
              </p>
              <p className="text-slate-300 mb-4">
                You understand and agree that the Service is provided "AS-IS" and that TradeRewind assumes no responsibility for the timeliness, deletion, mis-delivery or failure to store any user communications or personalization settings.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">3. Your Registration Obligations</h2>
              <p className="text-slate-300 mb-4">
                In consideration of your use of the Service, you agree to: (a) provide true, accurate, current and complete information about yourself as prompted by the Service's registration form and (b) maintain and promptly update your registration data to keep it true, accurate, current and complete.
              </p>
              <p className="text-slate-300 mb-4">
                If you provide any information that is untrue, inaccurate, not current or incomplete, or TradeRewind has reasonable grounds to suspect that such information is untrue, inaccurate, not current or incomplete, TradeRewind has the right to suspend or terminate your account and refuse any and all current or future use of the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">4. Privacy Policy</h2>
              <p className="text-slate-300 mb-4">
                Registration data and certain other information about you are subject to our Privacy Policy. For more information, see our full privacy policy at TradeRewind Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">5. Member Account, Password and Security</h2>
              <p className="text-slate-300 mb-4">
                You will receive a password and account designation upon completing the Service's registration process. You are responsible for maintaining the confidentiality of the password and account, and are fully responsible for all activities that occur under your password or account.
              </p>
              <p className="text-slate-300 mb-4">
                You agree to (a) immediately notify TradeRewind of any unauthorized use of your password or account or any other breach of security, and (b) ensure that you exit from your account at the end of each session. TradeRewind cannot and will not be liable for any loss or damage arising from your failure to comply with this Section.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">6. Member Conduct</h2>
              <p className="text-slate-300 mb-4">
                You understand that all information, data, text, software, graphics or other materials ("Content"), whether publicly posted or privately transmitted, are the sole responsibility of the person from which such Content originated. This means that you, and not TradeRewind, are entirely responsible for all Content that you upload, post, email, transmit or otherwise make available via the Service.
              </p>
              <p className="text-slate-300 mb-4">
                You agree to not use the Service to:
              </p>
              <ul className="list-disc list-inside text-slate-300 mb-4 space-y-2">
                <li>Upload, post, email, transmit or otherwise make available any Content that is unlawful, harmful, threatening, abusive, harassing, tortious, defamatory, vulgar, obscene, libelous, invasive of another's privacy, hateful, or racially, ethnically or otherwise objectionable</li>
                <li>Harm minors in any way</li>
                <li>Impersonate any person or entity, including, but not limited to, a TradeRewind official, or falsely state or otherwise misrepresent your affiliation with a person or entity</li>
                <li>Upload, post, email, transmit or otherwise make available any Content that you do not have a right to make available under any law or under contractual or fiduciary relationships</li>
                <li>Upload, post, email, transmit or otherwise make available any Content that infringes any patent, trademark, trade secret, copyright or other proprietary rights of any party</li>
                <li>Upload, post, email, transmit or otherwise make available any unsolicited or unauthorized advertising, promotional materials, "junk mail," "spam," "chain letters," "pyramid schemes," or any other form of solicitation</li>
                <li>Upload, post, email, transmit or otherwise make available any material that contains software viruses or any other computer code, files or programs designed to interrupt, destroy or limit the functionality of any computer software or hardware or telecommunications equipment</li>
                <li>Interfere with or disrupt the Service or servers or networks connected to the Service</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">7. Financial Disclaimer</h2>
              <p className="text-slate-300 mb-4">
                <strong className="text-white">IMPORTANT:</strong> The information provided by TradeRewind is for informational and educational purposes only. It should not be considered as financial advice, investment advice, trading advice, or any other sort of advice.
              </p>
              <p className="text-slate-300 mb-4">
                Trading and investing in financial markets involves substantial risk of loss and is not suitable for every investor. Past performance is not indicative of future results. You should carefully consider your financial situation and consult with financial advisors before making any investment decisions.
              </p>
              <p className="text-slate-300 mb-4">
                TradeRewind does not guarantee the accuracy, completeness, or timeliness of any information provided through the Service. Market data may be delayed or inaccurate. You acknowledge that you are using the Service at your own risk.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">8. Intellectual Property Rights</h2>
              <p className="text-slate-300 mb-4">
                The Service and its original content, features and functionality are and will remain the exclusive property of TradeRewind and its licensors. The Service is protected by copyright, trademark, and other laws of both the United States and foreign countries.
              </p>
              <p className="text-slate-300 mb-4">
                Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of TradeRewind.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">9. Termination</h2>
              <p className="text-slate-300 mb-4">
                TradeRewind may terminate or suspend your account and bar access to the Service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever and without limitation, including but not limited to a breach of the Terms.
              </p>
              <p className="text-slate-300 mb-4">
                If you wish to terminate your account, you may simply discontinue using the Service or contact our support team.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">10. Limitation of Liability</h2>
              <p className="text-slate-300 mb-4">
                In no event shall TradeRewind, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from:
              </p>
              <ul className="list-disc list-inside text-slate-300 mb-4 space-y-2">
                <li>Your access to or use of or inability to access or use the Service</li>
                <li>Any conduct or content of any third party on the Service</li>
                <li>Any content obtained from the Service</li>
                <li>Unauthorized access, use or alteration of your transmissions or content</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">11. Disclaimer</h2>
              <p className="text-slate-300 mb-4">
                Your use of the Service is at your sole risk. The Service is provided on an "AS IS" and "AS AVAILABLE" basis. The Service is provided without warranties of any kind, whether express or implied, including, but not limited to, implied warranties of merchantability, fitness for a particular purpose, non-infringement or course of performance.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">12. Governing Law</h2>
              <p className="text-slate-300 mb-4">
                These Terms shall be governed and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">13. Changes to Terms</h2>
              <p className="text-slate-300 mb-4">
                We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material we will provide at least 30 days notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
              </p>
              <p className="text-slate-300 mb-4">
                By continuing to access or use our Service after any revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, you are no longer authorized to use the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">14. Contact Us</h2>
              <p className="text-slate-300 mb-4">
                If you have any questions about these Terms, please contact us at:
              </p>
              <p className="text-slate-300">
                <strong className="text-white">TradeRewind</strong><br />
                Email: support@tradevision.ai<br />
                Website: www.tradevision.ai
              </p>
            </section>

            <div className="mt-8 pt-6 border-t border-slate-700">
              <p className="text-slate-400 text-sm">
                Last updated: January 16, 2026
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServiceModal;
